import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { notification, Button } from 'antd';
import {
  ExclamationCircleFilled,
  CloseCircleFilled,
  ArrowRightOutlined,
  IdcardOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { getStudentProfileStatus } from '../../../../Constants/Api/functions/function';
import './style.scss';

const StudentProfileReminder = ({ onStatusLoaded }) => {
  const userProfile = useSelector((state) => state.userProfile?.userProfile);
  const history = useHistory();
  const location = useLocation();

  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isStudent = userProfile?.role === 'student';
  const userId = userProfile?.id;

  const checkStatus = async () => {
    if (!userId || !isStudent) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await getStudentProfileStatus(userId);
      if (res && res.success) {
        setStatusData(res);
        if (onStatusLoaded) {
          onStatusLoaded(res);
        }

        // Notify if not completed every time
        if (!res.isCompleted) {
          showNotification(res);
        }
      }
    } catch (err) {
      console.error('Error checking profile status in reminder:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isStudent]);

  const showNotification = (data) => {
    const isRejected = data.status === 'rejected' || data.hasRejectedDocs;

    notification.open({
      key: 'student-profile-incomplete-notification',
      className: 'student-profile-reminder-popup',
      message: (
        <span style={{ fontWeight: 700, fontSize: '0.98rem', color: isRejected ? '#991b1b' : '#92400e' }}>
          {isRejected ? 'Action Required: Verification Revision Needed' : 'Action Required: Complete Your Student Profile'}
        </span>
      ),
      description: (
        <div className="popup-body">
          <p className="popup-desc">
            {isRejected
              ? `Your student verification was rejected: "${data.rejectionNotes || 'One or more documents need revision.'}". Please update and re-submit.`
              : 'Your 12-section profile & certificate information is not completed yet. You must complete and submit your profile to receive your verified credentials.'}
          </p>
          <div className="popup-actions">
            <Button
              type="primary"
              size="middle"
              className={isRejected ? 'btn-popup-rejected' : 'btn-popup-incomplete'}
              onClick={() => {
                if (notification && typeof notification.destroy === 'function') {
                  notification.destroy('student-profile-incomplete-notification');
                }
                history.push('/dashboard/student-profile');
              }}
            >
              {isRejected ? 'Review & Resubmit' : 'Complete Profile Now'} <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      ),
      icon: isRejected ? (
        <CloseCircleFilled style={{ color: '#ef4444', fontSize: 22 }} />
      ) : (
        <ExclamationCircleFilled style={{ color: '#f59e0b', fontSize: 22 }} />
      ),
      duration: 0, // Stays until user clicks or dismisses
      placement: 'topRight',
    });
  };

  if (!isStudent || !statusData || statusData.isCompleted) {
    return null;
  }

  // If student is currently on the student-profile page, don't show the duplicate top banner
  if (location.pathname === '/dashboard/student-profile') {
    return null;
  }

  const isRejected = statusData.status === 'rejected' || statusData.hasRejectedDocs;

  return (
    <div className={`student-profile-reminder-banner ${isRejected ? 'rejected' : 'incomplete'}`}>
      <div className="banner-content-wrap">
        <div className="banner-icon-badge">
          {isRejected ? <WarningOutlined /> : <IdcardOutlined />}
        </div>
        <div className="banner-text-group">
          <div className="banner-title-row">
            <span className="banner-alert-tag">{isRejected ? 'ACTION REQUIRED' : 'PROFILE INCOMPLETE'}</span>
            <strong className="banner-headline">
              {isRejected
                ? 'Your verification was returned for revision.'
                : 'Please complete your Student Profile & Certificate Verification.'}
            </strong>
          </div>
          <p className="banner-detail">
            {isRejected
              ? `Feedback: ${statusData.rejectionNotes || 'One or more submitted documents or fields need correction.'}`
              : 'Submit your legal identification, certificate printing name, and verification documents to ensure prompt academic verification.'}
          </p>
        </div>
      </div>

      <div className="banner-action-wrap">
        <Button
          type="primary"
          size="middle"
          className="banner-cta-button"
          onClick={() => history.push('/dashboard/student-profile')}
        >
          {isRejected ? 'Fix & Resubmit Now' : 'Complete Profile Now'} <ArrowRightOutlined />
        </Button>
      </div>
    </div>
  );
};

export default StudentProfileReminder;
